import axios from 'axios'

// Minimal Facebook-login session store. The backend (api.dulap.md) owns the
// OAuth dance; this module only starts the redirect, exchanges the one-time
// callback code for a JWT pair and keeps the pair fresh in localStorage.
// API paths are relative like the rest of the codebase (vite dev proxy /
// same-origin routing in prod).

export type AuthUser = {
  id: string
  email: string
  role: string
}

export type AuthSession = {
  accessToken: string
  refreshToken: string
  user: AuthUser
}

const ACCESS_KEY = 'auth_access_token'
const REFRESH_KEY = 'auth_refresh_token'
const USER_KEY = 'auth_user'
// Phone the visitor entered after Facebook login. Facebook never provides a
// phone number, and the backend promotes a webchat visitor to a CRM client
// only when identify() carries a phone — so we ask for it ourselves and keep
// it alongside the session.
const PHONE_KEY = 'auth_phone'
// sessionStorage: where to send the visitor after the OAuth round-trip.
const RETURN_KEY = 'auth_return_path'

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}
const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* ignore */
  }
}
const remove = (key: string) => {
  try {
    localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

const readSession = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}
const writeSessionStore = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    /* ignore */
  }
}
const removeSessionStore = (key: string) => {
  try {
    sessionStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

function storeSession(session: AuthSession) {
  write(ACCESS_KEY, session.accessToken)
  write(REFRESH_KEY, session.refreshToken)
  write(USER_KEY, JSON.stringify(session.user))
}

function clearSession() {
  remove(ACCESS_KEY)
  remove(REFRESH_KEY)
  remove(USER_KEY)
  remove(PHONE_KEY)
}

/** Phone entered after Facebook login (webchat identify contact). */
export function readStoredPhone(): string | null {
  return read(PHONE_KEY)
}

export function storePhone(phone: string) {
  write(PHONE_KEY, phone)
}

/**
 * Full-page navigation into the backend OAuth start. Must NOT be a fetch/XHR:
 * an httpOnly nonce cookie is set on this hop and verified on the callback.
 */
export function startFacebookLogin(returnPath: string) {
  writeSessionStore(RETURN_KEY, returnPath)
  window.location.href = '/api/auth/facebook'
}

/** Return path saved before the OAuth redirect; consumed by the callback. */
export function consumeReturnPath(): string {
  const path = readSession(RETURN_KEY) || '/'
  removeSessionStore(RETURN_KEY)
  return path
}

/**
 * Last logged-in user from localStorage, without a network round-trip. The
 * token pair may have expired — fine for display/identify purposes; use
 * getValidUser() when the session must actually be valid.
 */
export function readStoredUser(): AuthUser | null {
  const raw = read(USER_KEY)
  if (!raw) return null
  try {
    const user = JSON.parse(raw) as AuthUser
    return user && typeof user.email === 'string' ? user : null
  } catch {
    return null
  }
}

/**
 * Exchange the one-time callback code (60s TTL, single use) for a token pair.
 * Throws on failure — the callback page maps that to a retry state.
 */
export async function exchangeCode(code: string): Promise<AuthSession> {
  const { data } = await axios.post<AuthSession>('/api/auth/exchange', { code })
  storeSession(data)
  return data
}

const isUnauthorized = (err: unknown) =>
  axios.isAxiosError(err) && err.response?.status === 401

async function fetchMe(accessToken: string): Promise<AuthUser> {
  const { data } = await axios.get<AuthUser>('/api/auth/me', {
    headers: { Authorization: `Bearer ${accessToken}` },
  })
  return data
}

/**
 * Current logged-in user, or null. Access token TTL is short (~15m): on a 401
 * from /me try one refresh (rotated pair), then give up and clear the session.
 */
export async function getValidUser(): Promise<AuthUser | null> {
  const access = read(ACCESS_KEY)
  const refresh = read(REFRESH_KEY)
  if (!access || !refresh) return null

  try {
    return await fetchMe(access)
  } catch (err) {
    if (!isUnauthorized(err)) return null // network/server hiccup: keep tokens
  }

  try {
    const { data } = await axios.post<{
      accessToken: string
      refreshToken: string
    }>('/api/auth/refresh', { refreshToken: refresh })
    write(ACCESS_KEY, data.accessToken)
    write(REFRESH_KEY, data.refreshToken)
    return await fetchMe(data.accessToken)
  } catch {
    clearSession()
    return null
  }
}

/** Display name for a Facebook-identified visitor: the email local part. */
export function displayNameFromUser(user: AuthUser): string {
  return user.email.split('@')[0] || user.email
}
